import React, { useState } from 'react';
import './SearchPlayerPage.css';
import { getErrorMessage } from '../utils/api';
import { buildApiUrl } from '../config/api';

interface SearchResult {
  player_id: number;
  first_name: string;
  last_name: string;
  age: number | null;
  team: string | null;
  coach_name: string | null;
  latest_evaluation_id: number | null;
  latest_evaluation_date: string | null;
}

interface SearchPlayerPageProps {
  onBack: () => void;
  onEdit: (evaluationId: number) => void;
  onCreateNew: (playerId: number, playerName: string) => void;
  onViewDashboard: (playerId: number) => void;
}

// Using buildApiUrl helper instead of API_BASE_URL to ensure correct /api prefix

const SearchPlayerPage: React.FC<SearchPlayerPageProps> = ({
  onBack,
  onEdit,
  onCreateNew,
  onViewDashboard,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = (): string | null => {
    return localStorage.getItem('access_token');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setError(null);

    const token = getToken();
    if (!token) {
      setError('Authentication required');
      setSearchLoading(false);
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`/evaluations/search-players?query=${encodeURIComponent(searchQuery)}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.players || []);
      } else {
        const errorData = await response.json();
        setError(getErrorMessage(errorData, 'Search failed'));
        setSearchResults([]);
      }
    } catch (err) {
      setError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Age is already calculated by backend

  return (
    <div className="search-player-page">
      <div className="search-player-container">
        <h2>Search for Existing Player</h2>
        
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by first name, last name, or full name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="search-input"
            autoComplete="off"
          />
          <button 
            onClick={handleSearch} 
            className="search-btn"
            disabled={searchLoading}
          >
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {searchResults.length > 0 && (
          <div className="search-results">
            <h3>Search Results ({searchResults.length})</h3>
            <div className="results-list">
              {searchResults.map((player) => {
                return (
                  <div key={player.player_id} className="result-item">
                    <div className="result-info">
                      <div className="result-name">
                        {player.first_name} {player.last_name}
                      </div>
                      <div className="result-details">
                        <span>Age: {player.age || 'N/A'}</span>
                        <span>Team: {player.team || 'N/A'}</span>
                        <span>Coach: {player.coach_name || 'N/A'}</span>
                        {player.latest_evaluation_date && (
                          <span>Last Evaluation: {formatDate(player.latest_evaluation_date)}</span>
                        )}
                      </div>
                    </div>
                    <div className="result-actions">
                      {player.latest_evaluation_id && (
                        <button
                          className="action-btn edit-btn"
                          onClick={() => onEdit(player.latest_evaluation_id!)}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        className="action-btn create-btn"
                        onClick={() => onCreateNew(player.player_id, `${player.first_name} ${player.last_name}`)}
                      >
                        Create New Evaluation
                      </button>
                      <button
                        className="action-btn dashboard-btn"
                        onClick={() => onViewDashboard(player.player_id)}
                      >
                        Dashboard
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {searchQuery && searchResults.length === 0 && !searchLoading && !error && (
          <div className="no-results">No players found matching your search.</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
          <button onClick={onBack} className="exit-btn" style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            backgroundColor: '#383b48',
            color: '#fdfeff',
            border: '1px solid #567ad8',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#567ad8';
            e.currentTarget.style.borderColor = '#567ad8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#383b48';
            e.currentTarget.style.borderColor = '#567ad8';
          }}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchPlayerPage;

